import { addGenericResource } from '../../../../api/genericResource'
import { discardIssuedUrls } from '../../../../utils/directCloudUpload'
import { buildGenericResourceWallpaperCoForCreate } from '../buildGenericResourceWallpaperCo'
import { stickerNameFromFile } from '../form/stickerResourceName'
import { extractErrorMessage } from './uploadErrorClassify'

function resourceNameFromRow(row, assetType) {
  const fileName = row.name || ''
  if (assetType === 'stickers') {
    return stickerNameFromFile(fileName)
  }
  return fileName.replace(/\.[^./\\]+$/, '')
}

/**
 * 生成落库幂等指纹：同会话内同一类型 + 资源名 + 云地址视为同一落库请求。
 * 用于防止连点「保存到后端」重复创建资源（后端暂无幂等接口时的前端兜底）。
 */
export function buildSaveFingerprint(assetType, row) {
  const resourceName = resourceNameFromRow(row, assetType)
  return `${assetType}::${resourceName}::${row.publicUrl || ''}`
}

/**
 * 已上云未落库的行离开列表时删掉云端对象：这个地址只有这一行引用，留着就是孤儿文件。
 * 落库中的行不删，在途请求可能随后成功并引用该地址。清空 publicUrl 让重复调用无副作用。
 */
function takeUnsavedCloudUrl(row) {
  if (!row || row.cloudStatus !== 'success' || !row.publicUrl) {
    return ''
  }
  if (!['idle', 'failed'].includes(row.saveStatus)) {
    return ''
  }
  const url = row.publicUrl
  row.publicUrl = ''
  row.cloudStatus = 'idle'
  return url
}

export function discardUnsavedCloudObjects(rows) {
  const urls = (rows || []).map(takeUnsavedCloudUrl).filter(Boolean)
  if (!urls.length) {
    return Promise.resolve()
  }
  return discardIssuedUrls(urls)
}

export function discardUnsavedCloudObject(row) {
  return discardUnsavedCloudObjects([row])
}

/**
 * 单行落库：调用现有 addGenericResource 接口，不触发上云。
 * 业务失败保留云端对象和 publicUrl，「重试落库」沿用同一地址。
 * @param {object} row 文件行
 * @param {object} options
 * @param {string} options.assetType
 * @param {number|string} options.defaultLockGroupId
 * @param {number|string} [options.authorId] 这一批统一的设计师，不选为空
 * @param {Set<string>} [options.savedFingerprints] 本会话已成功落库的指纹集合
 */
export async function saveRowToBackend(row, {
  assetType,
  defaultLockGroupId,
  authorId,
  savedFingerprints
}) {
  if (row.saveStatus === 'saving' || row.saveStatus === 'success') {
    return { ok: row.saveStatus === 'success', skipped: true }
  }
  if (row.cloudStatus !== 'success' || !row.publicUrl) {
    return { ok: false, skipped: true }
  }

  const fingerprint = buildSaveFingerprint(assetType, row)
  if (savedFingerprints && savedFingerprints.has(fingerprint)) {
    row.saveStatus = 'success'
    row.saveErrorMessage = ''
    return { ok: true, skipped: true, deduped: true }
  }

  row.saveStatus = 'saving'
  row.saveErrorMessage = ''

  try {
    const resourceName = resourceNameFromRow(row, assetType)
    const wallpaperCo = buildGenericResourceWallpaperCoForCreate({
      resourceName,
      assetType,
      jsonConfig: { url: row.publicUrl },
      defaultLockGroupId,
      dataSetLockGroupList: [],
      authorId
    })
    const payload = {
      dataJson: JSON.stringify({ WallpaperCo: wallpaperCo }),
      resourceAddress: ''
    }

    const res = await addGenericResource(payload, {
      resolveErrorDataOnBizError: true
    })

    if (res && res.success) {
      row.saveStatus = 'success'
      if (res.data != null) {
        row.savedResourceId = res.data
      }
      if (savedFingerprints) {
        savedFingerprints.add(fingerprint)
      }
      return { ok: true }
    }

    throw new Error((res && (res.errMessage || res.message)) || '保存失败')
  } catch (error) {
    row.saveStatus = 'failed'
    row.saveErrorMessage = extractErrorMessage(error)
    return { ok: false }
  }
}
